import React, { useCallback, useEffect, useState } from 'react'
import { lmsAdmin, failureText } from '../api'
import { usePresetAccess } from './access'

// Launcher versions (rapid-api-lms D-141): SCORM and cmi5 packages are a thin
// relay, and a hosted launcher (immutable versions v1, v2, ...) holds the
// LMS-facing logic. Staff pick the version per preset or per connection. Shown
// on the presets page, which is already staff-only.

const DEFAULT_VALUE = ''

// A 400's per-field words, else the service's sentence, else the fallback.
const errorText = (error, fallback) => {
  const fields = error && error.details && error.details.fields
  if (fields && fields.version) return `Version: ${fields.version}`
  if (error && error.status === 404) return 'That preset or connection was not found.'
  return failureText(error, fallback)
}

const VersionSelect = ({ id, label, value, versions, onChange, disabled }) => (
  <select
    id={id}
    className="form-select form-select-sm w-auto d-inline-block"
    aria-label={label}
    value={value}
    disabled={disabled}
    onChange={event => onChange(event.target.value)}
  >
    <option value={DEFAULT_VALUE}>Default</option>
    {versions.map(version => (
      <option key={version} value={version}>
        {version}
      </option>
    ))}
  </select>
)

const toVersion = value => (value === DEFAULT_VALUE ? null : value)

const runsText = runs => {
  if (!runs) return '—'
  const from = { connection: "the connection's pin", preset: "its preset's pin", default: 'the default' }[runs.source] || runs.source
  return `${runs.version} (${from})`
}

export const LauncherVersions = () => {
  const { canEdit } = usePresetAccess()
  const [player, setPlayer] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [choices, setChoices] = useState({})
  const [busy, setBusy] = useState(null)
  const [message, setMessage] = useState(null)
  const [pinForm, setPinForm] = useState({ id: '', version: '' })

  const load = useCallback(() => {
    return lmsAdmin
      .getPlayer()
      .then(result => {
        setPlayer(result || {})
        setLoadError(null)
      })
      .catch(error => setLoadError(error))
  }, [])
  useEffect(() => {
    load()
  }, [load])

  if (loadError) {
    return (
      <div className="alert alert-danger" role="alert" data-testid="launcher-versions-error">
        {failureText(loadError, "Couldn't load the launcher versions.")}
      </div>
    )
  }
  if (!player || !Array.isArray(player.versions)) return null

  const versions = player.versions
  const presets = Array.isArray(player.presets) ? player.presets : []
  const connections = Array.isArray(player.connections) ? player.connections : []

  const run = async (key, call, done) => {
    setBusy(key)
    setMessage(null)
    try {
      await call()
      setMessage({ tone: 'success', text: done })
      await load()
    } catch (error) {
      setMessage({ tone: 'danger', text: errorText(error, 'The change could not be saved. Try again in a moment.') })
    } finally {
      setBusy(null)
    }
  }

  const choiceFor = preset => (preset.id in choices ? choices[preset.id] : preset.pin || DEFAULT_VALUE)

  const savePreset = preset =>
    run(`preset-${preset.id}`, () => lmsAdmin.setPresetPlayer(preset.id, toVersion(choiceFor(preset))), `Saved the launcher version for ${preset.name}.`)

  const clearConnection = connection =>
    run(`connection-${connection.id}`, () => lmsAdmin.setConnectionPlayer(connection.id, null), `Cleared the pin on ${connection.name || `connection ${connection.id}`}.`)

  const pinVersion = versions.includes(pinForm.version) ? pinForm.version : versions[0]

  const pinConnection = event => {
    event.preventDefault()
    const id = String(pinForm.id).trim()
    if (!id) return
    run('pin', () => lmsAdmin.setConnectionPlayer(id, pinVersion), `Pinned connection ${id} to ${pinVersion}.`)
  }

  return (
    <section className="lms-launcher-versions mt-4" data-testid="launcher-versions" aria-labelledby="launcher-versions-title">
      <h2 id="launcher-versions-title" className="h5">
        Launcher versions
      </h2>
      <p className="sw-hint">
        Released versions: {versions.join(', ') || 'none'}. The default is {player.default || '—'}. Each launch uses the connection's pin,
        else its preset's, else the default. A change applies to the next launch; to roll back, set the pin back.
      </p>

      {message && (
        <div className={`alert alert-${message.tone}`} role={message.tone === 'danger' ? 'alert' : 'status'} data-testid="launcher-versions-message">
          {message.text}
        </div>
      )}

      <h3 className="h6">Presets</h3>
      <table className="table table-sm">
        <thead>
          <tr>
            <th scope="col">Preset</th>
            <th scope="col">Launcher version</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {presets.map(preset => (
            <tr key={preset.id} data-testid={`launcher-preset-${preset.id}`}>
              <td>{preset.name}</td>
              <td>
                <VersionSelect
                  id={`launcher-preset-select-${preset.id}`}
                  label={`Launcher version for ${preset.name}`}
                  value={choiceFor(preset)}
                  versions={versions}
                  disabled={!canEdit || busy !== null}
                  onChange={value => setChoices({ ...choices, [preset.id]: value })}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  disabled={!canEdit || busy !== null}
                  data-testid={`launcher-preset-save-${preset.id}`}
                  onClick={() => savePreset(preset)}
                >
                  Save
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="h6">Pinned connections</h3>
      {connections.length === 0 ? (
        <p className="sw-hint" data-testid="launcher-no-pins">
          No connection is pinned.
        </p>
      ) : (
        <table className="table table-sm">
          <thead>
            <tr>
              <th scope="col">Connection</th>
              <th scope="col">Pin</th>
              <th scope="col">Runs</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {connections.map(connection => (
              <tr key={connection.id} data-testid={`launcher-connection-${connection.id}`}>
                <td>
                  {connection.name || `Connection ${connection.id}`}
                  <span className="sw-hint ms-2">#{connection.id}</span>
                </td>
                <td>{connection.pin}</td>
                <td>{runsText(connection.runs)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    disabled={!canEdit || busy !== null}
                    data-testid={`launcher-connection-clear-${connection.id}`}
                    onClick={() => clearConnection(connection)}
                  >
                    Clear
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form className="d-flex flex-wrap align-items-center gap-2" onSubmit={pinConnection} data-testid="launcher-pin-form">
        <label htmlFor="launcher-pin-id" className="mb-0">
          Pin a connection by id
        </label>
        <input
          id="launcher-pin-id"
          type="number"
          min="1"
          className="form-control form-control-sm w-auto"
          value={pinForm.id}
          disabled={!canEdit}
          onChange={event => setPinForm({ ...pinForm, id: event.target.value })}
        />
        <select
          className="form-select form-select-sm w-auto"
          aria-label="Version to pin"
          value={pinVersion}
          disabled={!canEdit}
          onChange={event => setPinForm({ ...pinForm, version: event.target.value })}
        >
          {versions.map(version => (
            <option key={version} value={version}>
              {version}
            </option>
          ))}
        </select>
        <button type="submit" className="btn btn-light btn-sm" disabled={!canEdit || busy !== null || !String(pinForm.id).trim()}>
          Pin
        </button>
      </form>
    </section>
  )
}
